import { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import { BinaryModel } from '@ctw/shared/api/fhir/models/binary';
import { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import { fqsRequest } from '@ctw/shared/api/fqs/client';
import type { InferQueryResult } from '@ctw/shared/api/fqs/helpers';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import {
  SearchBinariesDocument,
  SearchDocumentReferencesDocument,
} from '@ctw/shared/types/graphql/__generated__/graphql';
import { QUERY_KEY_RESOURCE_SEARCH } from '@ctw/shared/utils/query-keys';
import type { UseQueryResult } from '@tanstack/react-query';
import type {
  AllergyIntolerance,
  Binary,
  Condition,
  DiagnosticReport,
  DocumentReference,
  MedicationStatement,
  Observation,
} from 'fhir/r4';
import { compact } from 'lodash-es';

export type PatientRecordSearchQueryDocumentResult = InferQueryResult<
  typeof SearchDocumentReferencesDocument
>;
export type PatientRecordSearchQueryBinaryResult = InferQueryResult<typeof SearchBinariesDocument>;
export type PatientRecordSearchQueryResult =
  | PatientRecordSearchQueryDocumentResult
  | PatientRecordSearchQueryBinaryResult;

export type PatientRecordSearchQuerySearchResultNode = NonNullable<
  PatientRecordSearchQueryResult['SearchConnection']
>['edges'][number]['node'];
export type PatientRecordSearchQuerySearchResultBinaryNode = NonNullable<
  PatientRecordSearchQueryBinaryResult['SearchConnection']
>['edges'][number]['node'];
export type PatientRecordSearchQueryMatchResult =
  PatientRecordSearchQuerySearchResultNode['matches'][number];

export const isPatientRecordSearchQueryBinaryNode = (
  node: PatientRecordSearchQuerySearchResultNode,
): node is PatientRecordSearchQuerySearchResultBinaryNode =>
  !Object.hasOwn(node, 'relatedResources');

export interface PatientRecordSearchResult
  extends Omit<PatientRecordSearchQuerySearchResultNode, 'resource'> {
  allMatches: Array<PatientRecordSearchQueryMatchResult>;
  resource:
    | DocumentModel
    | BinaryModel
    | AllergyModel
    | ConditionModel
    | MedicationStatementModel
    | DiagnosticReportModel
    | ObservationModel;
}

export interface PatientRecordSearchBinaryResult
  extends Omit<PatientRecordSearchQuerySearchResultBinaryNode, 'resource'> {
  allMatches: Array<PatientRecordSearchQueryMatchResult>;
  resource: BinaryModel;
}

export const isPatientRecordSearchBinaryResult = (
  result: PatientRecordSearchResult,
): result is PatientRecordSearchBinaryResult => result.resource instanceof BinaryModel;

export function usePatientRecordSearch(
  query: string,
  isExactMatch?: boolean,
): UseQueryResult<Array<PatientRecordSearchResult>> {
  const hasPatientRecordSearchBinariesFeature = useFeatureFlag('fqs-zap-search-binaries');
  const searchQueryParam = isExactMatch && query ? `"${query}"` : query;
  const searchQuery = hasPatientRecordSearchBinariesFeature
    ? SearchBinariesDocument
    : SearchDocumentReferencesDocument;

  return usePatientQuery({
    queryId: QUERY_KEY_RESOURCE_SEARCH,
    queryKey: [query, isExactMatch],
    queryFn: async ({ telemetry, graphqlClient, patient }) => {
      const { data } = await fqsRequest<PatientRecordSearchQueryResult>(
        telemetry,
        graphqlClient,
        searchQuery.toString(),
        {
          upid: patient.UPID,
          query: searchQueryParam,
          first: 500,
        },
      );

      return compact(
        data.SearchConnection?.edges.map((edge): PatientRecordSearchResult | undefined => {
          const relatedResources = isPatientRecordSearchQueryBinaryNode(edge.node)
            ? []
            : (edge.node.relatedResources?.flatMap((relatedResource) => relatedResource.matches) ??
              []);
          const allMatches: Array<PatientRecordSearchQueryMatchResult> = [
            ...edge.node.matches,
            ...relatedResources,
          ];

          let resource: PatientRecordSearchResult['resource'] | undefined;
          switch (edge.node.resourceType) {
            case 'DocumentReference':
              resource = new DocumentModel(edge.node.resource as DocumentReference);
              break;
            case 'Binary':
              resource = new BinaryModel(edge.node.resource as Binary);
              break;
            case 'AllergyIntolerance':
              resource = new AllergyModel(edge.node.resource as AllergyIntolerance);
              break;
            case 'Condition':
              resource = new ConditionModel(edge.node.resource as Condition);
              break;
            case 'MedicationStatement':
              resource = new MedicationStatementModel(edge.node.resource as MedicationStatement);
              break;
            case 'DiagnosticReport':
              resource = new DiagnosticReportModel(edge.node.resource as DiagnosticReport);
              break;
            case 'Observation':
              resource = new ObservationModel(edge.node.resource as Observation);
              break;
            default:
              // `result.node.resource` is a `GroupedSearchNode` which we can't handle unless we know
              // the type, so ignore it and filter it out
              resource = undefined;
              break;
          }

          return resource === undefined
            ? undefined
            : {
                ...edge.node,
                resource,
                allMatches,
              };
        }),
      );
    },
    enabled: !!query,
  });
}
